/**
 * Drug Search Form Component
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Grid, TextField, FormControlLabel, Switch, MenuItem, Chip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';

const DrugSearchForm = ({ filters, facets, onUpdateFilters, onSearch, onClear, autoSearch, onAutoSearchChange, loading }) => {
  const softInputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      borderRadius: '4px',
      height: '38px',
      transition: 'all 0.15s ease-in-out',
      '& fieldset': { borderColor: '#e2e8f0', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: '#cbd5e1' },
      '&.Mui-focused fieldset': { borderColor: '#2196f3', borderWidth: '1px' },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: '0 2px 6px rgba(33, 150, 243, 0.06)',
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 10px',
      
      color: '#0f172a',
      height: '32px',
      boxSizing: 'border-box',
      '&::placeholder': {
        
      },
      '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #ffffff inset !important', WebkitTextFillColor: '#0f172a !important' }
    },
    '& .MuiInputLabel-root': {  color: '#64748b' },
    '& .MuiSelect-select': {
      padding: '0 10px !important',
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      
      color: '#0f172a',
      boxSizing: 'border-box',
    }
  };

  const FieldLabel = ({ children }) => (
    <Typography sx={{ fontWeight: 600, color: '#475569', mb: 0.4, ml: 0.5,  lineHeight: 1.2, display: 'block' }}>
      {children}
    </Typography>
  );

  const handleChange = (field) => (event) => {
    onUpdateFilters({ [field]: event.target.value });
  };

  const handleClearField = (field) => () => {
    onUpdateFilters({ [field]: '' });
  };

  const hasActiveFilters = Boolean(filters.brandName || filters.brandType || filters.productSum);

  const chipDeleteIcon = (
    <CloseIcon
      sx={{
        fontSize: "15px !important",
        color: "#64748b",
        transition: "0.2s",
        mr: 1,
        cursor: "pointer",
        "&:hover": {
          color: "#ef4444",
        },
      }}
    />
  );

  const chipSx = { backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mb: 0 }}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3.5}>
          <FieldLabel>Drug Name</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth
            size="small"
            value={filters.name}
            onChange={handleChange('name')}
            disabled={loading}
            placeholder="Search..."
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FieldLabel>Brand Name</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth
            select
            size="small"
            value={filters.brandName}
            onChange={handleChange('brandName')}
            disabled={loading}
          >
            <MenuItem value="">Select</MenuItem>
            {(facets?.brandNames || []).map((item) => (
              <MenuItem key={item} value={item}>{item}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <FieldLabel>Brand Type</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth
            select
            size="small"
            value={filters.brandType}
            onChange={handleChange('brandType')}
            disabled={loading}
          >
            <MenuItem value="">Select</MenuItem>
            {(facets?.brandTypes || []).map((item) => (
              <MenuItem key={item} value={item}>{item}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <FieldLabel>Product Sum</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth
            select
            size="small"
            value={filters.productSum}
            onChange={handleChange('productSum')}
            disabled={loading}
          >
            <MenuItem value="">Select</MenuItem>
            {(facets?.productSums || []).map((item) => (
              <MenuItem key={item} value={item}>{item}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {hasActiveFilters && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', ml: 1 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, color: '#475569', mb: 0,  letterSpacing: '0.05em', lineHeight: 1, display: 'block' }}>
                Active Selection
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                {filters.brandName && (
                  <Chip
                    deleteIcon={chipDeleteIcon}
                    label={`Brand: ${filters.brandName}`}
                    size="small"
                    onDelete={handleClearField('brandName')}
                    sx={chipSx}
                  />
                )}
                {filters.brandType && (
                  <Chip
                    deleteIcon={chipDeleteIcon}
                    label={`Type: ${filters.brandType}`}
                    size="small"
                    onDelete={handleClearField('brandType')}
                    sx={chipSx}
                  />
                )}
                {filters.productSum && (
                  <Chip
                    deleteIcon={chipDeleteIcon}
                    label={`Sum: ${filters.productSum}`}
                    size="small"
                    onDelete={handleClearField('productSum')}
                    sx={chipSx}
                  />
                )}
                <Button className="action-btn cancel-btn"
                  variant="text"
                  size="small"
                  onClick={onClear}
                  disabled={loading}
                  sx={{ textTransform: 'none', fontWeight: 600, color: '#1E88E5',  minWidth: 'auto', p: 0, ml: 1 }}
                >
                  Reset All
                </Button>
              </Box>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={2.5}>
          <FieldLabel>&nbsp;</FieldLabel>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button className="action-btn process-btn" 
              type="submit" 
              variant="contained" 
              color="primary" 
              startIcon={<SearchIcon />} 
              disabled={loading} 
              fullWidth
              sx={{ height: '38px', borderRadius: '8px', fontWeight: 600, textTransform: 'none',  boxShadow: '0 2px 4px rgba(30, 136, 229, 0.2)' }}
            >
              Search
            </Button>
            <Button className="action-btn cancel-btn" 
              variant="outlined" 
              color="inherit" 
              startIcon={<ClearIcon />} 
              onClick={onClear} 
              disabled={loading}
              fullWidth
              sx={{ height: '38px', borderRadius: '8px', fontWeight: 600, textTransform: 'none',  color: '#64748b', borderColor: '#e2e8f0', '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#cbd5e1' } }}
            >
              Clear
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

DrugSearchForm.propTypes = {
  filters: PropTypes.object.isRequired,
  facets: PropTypes.shape({
    brandNames: PropTypes.array,
    brandTypes: PropTypes.array,
    productSums: PropTypes.array,
  }),
  onUpdateFilters: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  autoSearch: PropTypes.bool.isRequired,
  onAutoSearchChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default DrugSearchForm;
